import React, {
    FC, useCallback, useEffect, useState,
} from 'react';

import { observer } from 'mobx-react';
import classNames from 'classnames';

import Spinner from '../../../../../../Common/Spin';
import OpenWidget from '../../../../../../OpenViewer/OpenWidget';
import { hasViewerSession } from '../../../../../../OpenViewer/helpers';
import { usePxSize, useStores, useTargetScreen } from '@/components/hooks';

import ErrorView from './ErrorView';
import FolderView from './FolderView';
import { useSignatureSaving, useViewerState } from './hooks';
import { checkIsFileEditable } from './helpers';
import styles from './FileView.module.scss';
import './antOverride.scss';

const FileView: FC = observer(() => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isInitialLoading, setIsInitialLoading] = useState<boolean>(false);
    const isMobile = useTargetScreen('md');

    const { containerRef, size } = usePxSize<HTMLDivElement>();

    const {
        filesAccessStore,
        recipientPageStore,
        userStore,
    } = useStores();
    const { setFullScreen, setCollapsed, sharedFilesStore } = recipientPageStore;
    const { selectedFilePermissions, selectedFile } = sharedFilesStore;
    const { currentUserIdentity } = userStore;

    const { viewerState, errorType, contentType } = useViewerState({
        filesAccessStore,
        sharedFilesStore,
        selectedFile,
        selectedFilePermissions,
    });

    const isFileLoading = viewerState?.isLoading;
    const iframeParams = viewerState?.result;

    const showViewerWidget = contentType === 'viewer' && hasViewerSession(iframeParams);

    useEffect(() => {
        if (selectedFile?.file_id && !selectedFile.is_folder) {
            setIsInitialLoading(true);

            const hasViewerState = !!viewerState;
            const hasResult = !!iframeParams;
            const hasError = contentType === 'error';

            if (hasResult || hasError || (hasViewerState && isFileLoading)) {
                setIsInitialLoading(false);
            }
        } else {
            setIsInitialLoading(false);
        }
    }, [selectedFile?.file_id, selectedFile?.is_folder, viewerState, iframeParams, contentType, isFileLoading]);

    useEffect(() => {
        if (showViewerWidget || contentType === 'error') {
            setIsInitialLoading(false);
        }
    }, [showViewerWidget, contentType]);

    useEffect(() => {
        if (!recipientPageStore.fullScreen) {
            setFullScreen(showViewerWidget);
            setCollapsed(showViewerWidget);
        }
    }, [showViewerWidget]);

    useEffect(() => {
        if (isMobile && showViewerWidget) {
            document.body.classList.add('document-viewing');
        } else {
            document.body.classList.remove('document-viewing');
        }
    }, [isMobile, showViewerWidget]);

    useEffect(() => () => {
        document.body.classList.remove('document-viewing');
    }, []);

    const onSaveSignature = useSignatureSaving(filesAccessStore);
    const onSessionExpired = useCallback(() => {
        filesAccessStore.deleteAccessItem(filesAccessStore.selectedAccessFileId);
    }, [filesAccessStore]);

    return (
        <div className={styles.container} ref={containerRef}>
            <Spinner
                spinning={isFileLoading || isLoading || isInitialLoading}
                wrapperClassName={classNames(styles.spinner, 'file-view-spinner-wrapper')}
            >
                {contentType === 'error' && (
                    <ErrorView
                        errorType={errorType}
                    />
                )}
                {contentType === 'folder' && <FolderView />}
                {showViewerWidget && (
                    <OpenWidget
                        carouselMode
                        iframeParams={iframeParams}
                        setIsLoading={setIsLoading}
                        fileId={selectedFile.file_id}
                        filePolicyId={selectedFile.policy_id}
                        isEditable={checkIsFileEditable(
                            selectedFilePermissions,
                            currentUserIdentity,
                            { id: selectedFile.owner, email: selectedFile.owner_email },
                        )}
                        PDFViewerStyle={size}
                        onSaveSignature={onSaveSignature}
                        onSessionExpired={onSessionExpired}
                    />
                )}
            </Spinner>
        </div>
    );
});

export default FileView;
